import { HistoryOutlined } from "@ant-design/icons"
import { Alert, Button, Empty, Flex, Popover, Spin, Tag, Tooltip, Typography } from "antd"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope.ts"
import { getStatusTransitions } from "../api/status-automation.services.ts"
import {
  STATUS_HISTORY_PARAM,
  eventDisplay,
  isUndoAvailable,
  refusalMessage,
  statusDisplay
} from "../helpers/status-automation.ts"
import type {
  StatusRecordKind,
  StatusRefusal,
  StatusTransition
} from "../types/status-automation.types.ts"

const { Text } = Typography

const TIME_FORMAT = "MMM D, h:mm A"

export type StatusUndoResult =
  | { ok: true; transitions?: StatusTransition[] }
  | { ok: false; refusal: StatusRefusal | null }

interface StatusHistoryPopoverProps {
  kind: StatusRecordKind
  recordId: string
  /** The right a manual change back would need; Undo is hidden without it. */
  canUndo: boolean
  onUndo: (transition: StatusTransition) => Promise<StatusUndoResult>
}

export function StatusHistoryPopover({
  kind,
  recordId,
  canUndo,
  onUndo
}: StatusHistoryPopoverProps) {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [open, setOpen] = useState(
    searchParams.get(STATUS_HISTORY_PARAM) === "open"
  )
  const [rows, setRows] = useState<StatusTransition[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [undoingId, setUndoingId] = useState<string | null>(null)
  const [refusal, setRefusal] = useState<string | null>(null)
  const request = useRef(0)

  const load = useCallback(async () => {
    const mine = ++request.current
    setLoading(true)
    setFailed(false)
    const response = await getStatusTransitions(kind, recordId)
    if (mine !== request.current) return
    setLoading(false)
    if (!response.ok) {
      setFailed(true)
      return
    }
    const data = unwrapEnvelope<StatusTransition[]>(response.data)
    setRows(Array.isArray(data) ? data : [])
  }, [kind, recordId])

  useEffect(() => {
    if (open) void load()
  }, [open, load])

  // A different record answers with a different history.
  useEffect(() => {
    setRows(null)
    setRefusal(null)
  }, [kind, recordId])

  const undo = async (transition: StatusTransition) => {
    setUndoingId(transition.id)
    setRefusal(null)
    try {
      const result = await onUndo(transition)
      if (result.ok) {
        if (result.transitions) setRows(result.transitions)
        else await load()
        return
      }
      if (result.refusal) {
        setRefusal(refusalMessage(result.refusal, t))
        await load()
      }
    } finally {
      setUndoingId(null)
    }
  }

  const content = (
    <div className={"w-[360px] max-w-[80vw]"} data-testid={"status-history"}>
      {refusal && (
        <Alert
          type={"warning"}
          showIcon
          className={"mb-2"}
          message={refusal}
          data-testid={"status-history-refusal"}
        />
      )}
      {failed ? (
        <Flex vertical gap={8} align={"start"}>
          <Text type={"secondary"}>{t("statusAutomation.history.error")}</Text>
          <Button size={"small"} onClick={() => void load()}>
            {t("statusAutomation.history.retry")}
          </Button>
        </Flex>
      ) : loading && !rows ? (
        <Flex justify={"center"} className={"py-4"}>
          <Spin size={"small"} />
        </Flex>
      ) : !rows?.length ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("statusAutomation.history.empty")}
        />
      ) : (
        <ul className={"m-0 flex max-h-80 list-none flex-col gap-2 overflow-y-auto p-0"}>
          {rows.map((row) => (
            <StatusHistoryRow
              key={row.id}
              kind={kind}
              row={row}
              canUndo={canUndo}
              undoing={undoingId === row.id}
              locked={!!undoingId}
              onUndo={() => void undo(row)}
            />
          ))}
        </ul>
      )}
    </div>
  )

  return (
    <Popover
      trigger={"click"}
      placement={"bottomLeft"}
      open={open}
      onOpenChange={setOpen}
      title={t("statusAutomation.history.title")}
      content={content}
    >
      <Tooltip title={t("statusAutomation.history.title")}>
        <Button
          size={"small"}
          type={"text"}
          icon={<HistoryOutlined />}
          aria-label={t("statusAutomation.history.title")}
          data-testid={"status-history-trigger"}
        />
      </Tooltip>
    </Popover>
  )
}

interface RowProps {
  kind: StatusRecordKind
  row: StatusTransition
  canUndo: boolean
  undoing: boolean
  locked: boolean
  onUndo: () => void
}

function StatusHistoryRow({
  kind,
  row,
  canUndo,
  undoing,
  locked,
  onUndo
}: RowProps) {
  const { t } = useTranslation()
  const { formatDate } = useDateFormatter()
  const status = statusDisplay(kind, row.status, t)
  const event = eventDisplay(row.event, t, row.source_label, row.source_category)
  const when = formatDate(row.occurred_at, TIME_FORMAT)

  let headline: string
  if (row.is_automatic) {
    headline = t("statusAutomation.history.movedAutomatically", { status })
  } else if (row.source === "undo") {
    headline = t("statusAutomation.history.movedBack", { status })
  } else {
    headline = t("statusAutomation.history.moved", { status })
  }

  const meta = [
    row.is_automatic ? event : row.actor?.name,
    when
  ].filter(Boolean)

  const undoable = canUndo && !row.undone_at && isUndoAvailable(row)

  return (
    <li
      data-testid={`status-history-row-${row.id}`}
      className={"flex items-start justify-between gap-2 border-b border-gray-100 pb-2 last:border-b-0"}
    >
      <Flex vertical gap={2} className={"min-w-0"}>
        <Text strong className={"text-sm"}>
          {headline}
        </Text>
        <Text type={"secondary"} className={"text-xs"}>
          {meta.join(" · ")}
        </Text>
      </Flex>
      {row.undone_at ? (
        <Tag className={"m-0"}>{t("statusAutomation.history.undone")}</Tag>
      ) : undoable ? (
        <Tooltip
          title={t("statusAutomation.history.undoUntil", {
            time: formatDate(row.undo_available_until, TIME_FORMAT)
          })}
        >
          <Button
            size={"small"}
            loading={undoing}
            disabled={locked && !undoing}
            onClick={onUndo}
          >
            {t("statusAutomation.history.undo")}
          </Button>
        </Tooltip>
      ) : null}
    </li>
  )
}
